const DEFAULT_EPSILON = 1e-10;

const toFiniteNumber = (value) => {
  if (
    (typeof value !== "number" && typeof value !== "string") ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return null;
  }

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const isValidCoordinateRange = (lat, lng) =>
  lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

export const normalizeLatLng = (value) => {
  if (Array.isArray(value) && value.length >= 2) {
    const lat = toFiniteNumber(value[0]);
    const lng = toFiniteNumber(value[1]);

    if (lat !== null && lng !== null && isValidCoordinateRange(lat, lng)) {
      return { lat, lng };
    }
  }

  const lat = toFiniteNumber(value?.lat ?? value?.latitude);
  const lng = toFiniteNumber(value?.lng ?? value?.longitude);

  if (lat !== null && lng !== null && isValidCoordinateRange(lat, lng)) {
    return { lat, lng };
  }

  return null;
};

export const normalizePolygonCoordinates = (polygon) => {
  if (!Array.isArray(polygon)) {
    return [];
  }

  let ring = polygon;

  while (
    Array.isArray(ring?.[0]) &&
    ring[0].length > 0 &&
    (Array.isArray(ring[0][0]) || normalizeLatLng(ring[0][0]))
  ) {
    ring = ring[0];
  }

  const isLegacySingletonRing =
    ring.length >= 6 &&
    ring.length % 2 === 0 &&
    ring.every(
      (coordinate) =>
        Array.isArray(coordinate) &&
        coordinate.length === 1 &&
        toFiniteNumber(coordinate[0]) !== null,
    );

  if (isLegacySingletonRing) {
    ring = Array.from({ length: ring.length / 2 }, (_, index) => [
      ring[index * 2][0],
      ring[index * 2 + 1][0],
    ]);
  }

  return ring.map(normalizeLatLng).filter(Boolean);
};

const isPointOnSegment = (point, start, end, epsilon = DEFAULT_EPSILON) => {
  const cross =
    (point.lng - start.lng) * (end.lat - start.lat) -
    (point.lat - start.lat) * (end.lng - start.lng);

  if (Math.abs(cross) > epsilon) {
    return false;
  }

  return (
    point.lng >= Math.min(start.lng, end.lng) - epsilon &&
    point.lng <= Math.max(start.lng, end.lng) + epsilon &&
    point.lat >= Math.min(start.lat, end.lat) - epsilon &&
    point.lat <= Math.max(start.lat, end.lat) + epsilon
  );
};

export const isPointInPolygon = (
  point,
  polygon,
  { includeBoundary = true } = {},
) => {
  const normalizedPoint = normalizeLatLng(point);
  const normalizedPolygon = normalizePolygonCoordinates(polygon);

  if (!normalizedPoint || normalizedPolygon.length < 3) {
    return false;
  }

  let inside = false;
  const x = normalizedPoint.lng;
  const y = normalizedPoint.lat;

  for (
    let i = 0, j = normalizedPolygon.length - 1;
    i < normalizedPolygon.length;
    j = i++
  ) {
    const current = normalizedPolygon[i];
    const previous = normalizedPolygon[j];

    if (
      includeBoundary &&
      isPointOnSegment(normalizedPoint, previous, current)
    ) {
      return true;
    }

    const intersects =
      (current.lat > y) !== (previous.lat > y) &&
      x <
        ((previous.lng - current.lng) * (y - current.lat)) /
          (previous.lat - current.lat) +
          current.lng;

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
};

export const filterItemsWithinPolygon = (
  items = [],
  polygon,
  getPosition = (item) => item?.position,
) => {
  if (!Array.isArray(items)) {
    return [];
  }

  const normalizedPolygon = normalizePolygonCoordinates(polygon);

  if (normalizedPolygon.length < 3) {
    return items;
  }

  return items.filter((item) =>
    isPointInPolygon(getPosition(item), normalizedPolygon),
  );
};
